import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import readXlsxFile from "read-excel-file";
import {
  PLATFORMS,
  type KocListItem,
  type Platform,
  type ProjectListItem,
  type VideoImportItem,
  type VideoImportResponse,
  type VideoImportRowResult,
} from "@koc/shared";
import { apiKocs, apiProjects, apiVideos } from "../api.js";
import { Badge, Button, Card, Input } from "../components/ui.js";

// A draft row in the import dialog. `project_id` is null until the user picks one (or the
// CSV/XLSX parser auto-resolves a name to an existing project). For the KOC, the user can
// either pick an existing one (`koc_id`) or type a new name (`koc_name_typed`) which the
// server auto-creates on submit. Submission requires exactly one of those to be set.
// `pendingProjectName` and `pendingKocName` are unresolved strings carried from CSV/XLSX
// parse so the picker can show "couldn't match X" until the user fixes it.
type DraftRow = {
  project_id: number | null;
  pendingProjectName: string | null;
  koc_id: number | null;
  koc_name_typed: string;
  pendingKocName: string | null;
  platform: Platform;
  page_label: string;
  url: string;
};

function emptyRow(): DraftRow {
  return {
    project_id: null,
    pendingProjectName: null,
    koc_id: null,
    koc_name_typed: "",
    pendingKocName: null,
    platform: "tiktok",
    page_label: "",
    url: "",
  };
}

// Recognised header aliases. Comparison is case-insensitive after trim. `project/platform/url`
// are required; `koc` and `label` are optional.
const HEADER_ALIASES = {
  project: ["project", "creator", "creator_name", "username", "user", "user_name", "handle", "account", "account_name", "name", "项目", "用户名", "账号", "账号名"],
  koc: ["koc", "koc_name", "publisher", "influencer", "kol", "creator_person", "达人", "博主"],
  platform: ["platform", "site", "source", "平台"],
  label: ["label", "page_label", "account_label", "tag", "标签", "页面标签"],
  url: ["url", "video_url", "link", "video", "video link", "视频链接", "链接"],
};

type HeaderKey = "project" | "koc" | "platform" | "label" | "url";

function matchHeader(h: string): HeaderKey | null {
  const norm = h.trim().toLowerCase();
  for (const [key, aliases] of Object.entries(HEADER_ALIASES) as [
    keyof typeof HEADER_ALIASES,
    string[],
  ][]) {
    if (aliases.includes(norm)) return key;
  }
  return null;
}

function normalizePlatform(raw: string): Platform | null {
  const v = raw.trim().toLowerCase();
  if (v === "tiktok" || v === "tt" || v === "douyin") return "tiktok";
  if (v === "instagram" || v === "ig" || v === "insta") return "instagram";
  if (v === "youtube" || v === "yt" || v === "ytshorts") return "youtube";
  return PLATFORMS.includes(v as Platform) ? (v as Platform) : null;
}

// Parse a CSV line respecting double-quoted fields with "" escaping. We don't pull in a CSV
// lib for this — the format is well-defined and the input volume is small.
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out;
}

// Per-row URL pre-validation that mirrors the server's `z.string().url().max(2000)` check
// but with a more specific message so the user can fix the offending input without having
// to bisect through the row list. Returns null when OK or when empty (empty rows are
// filtered separately via `validRowsWithIdx`).
//   - whitespace inside the URL → typically two URLs accidentally pasted into one field
//   - zero-width / NBSP unicode → some chat apps inject these on copy
//   - missing http(s):// scheme
//   - new URL() throws → outright malformed
function validateUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length > 2000) return "URL is too long (max 2000 chars).";
  if (/[\u200B-\u200D\uFEFF\u00A0]/.test(trimmed)) {
    return "URL contains an invisible character (zero-width / non-breaking space). Retype it.";
  }
  if (/\s/.test(trimmed)) {
    if (/https?:\/\/[^\s]+\s+https?:\/\//i.test(trimmed)) {
      return "Looks like multiple URLs were pasted into one field — split them across separate rows.";
    }
    return "URL contains whitespace inside it.";
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return "URL must start with http:// or https://.";
  }
  try {
    new URL(trimmed);
  } catch {
    return "URL is malformed.";
  }
  return null;
}

type ParsedTabularRow = {
  project: string;
  koc: string;
  platform: Platform;
  label: string;
  url: string;
};

// Resolve a project name (string from CSV/XLSX) to an existing project id by case-insensitive
// exact match against `account_name`. Returns null if zero or multiple matches.
function resolveProjectName(name: string, projects: ProjectListItem[]): ProjectListItem | null {
  const norm = name.trim().toLowerCase();
  if (!norm) return null;
  const matches = projects.filter((c) => c.account_name.trim().toLowerCase() === norm);
  return matches.length === 1 ? (matches[0] ?? null) : null;
}

function resolveKocName(name: string, kocs: KocListItem[]): KocListItem | null {
  const norm = name.trim().toLowerCase();
  if (!norm) return null;
  const matches = kocs.filter((k) => k.name.trim().toLowerCase() === norm);
  return matches.length === 1 ? (matches[0] ?? null) : null;
}

function tabularToDrafts(
  parsed: ParsedTabularRow[],
  projects: ProjectListItem[],
  kocs: KocListItem[],
): { rows: DraftRow[]; unresolvedProjects: number } {
  const rows: DraftRow[] = [];
  let unresolvedProjects = 0;
  for (const p of parsed) {
    const matchedProject = resolveProjectName(p.project, projects);
    const matchedKoc = p.koc ? resolveKocName(p.koc, kocs) : null;

    if (!matchedProject) unresolvedProjects++;
    rows.push({
      project_id: matchedProject?.id ?? null,
      pendingProjectName: matchedProject ? null : p.project,
      koc_id: matchedKoc?.id ?? null,
      koc_name_typed: matchedKoc ? "" : p.koc,
      pendingKocName: matchedKoc || !p.koc ? null : p.koc,
      platform: p.platform,
      page_label: p.label,
      url: p.url,
    });
  }
  return { rows, unresolvedProjects };
}

// Inline searchable project picker — typeahead over the loaded project list, click to
// select. Shows the project's keyword count next to each option so the user can see who
// has follow-up scheduling configured.
function ProjectPicker({
  projects,
  value,
  pendingName,
  onChange,
}: {
  projects: ProjectListItem[];
  value: number | null;
  pendingName: string | null;
  onChange: (projectId: number | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = projects.find((c) => c.id === value) ?? null;

  useEffect(() => {
    if (selected) setSearch("");
  }, [selected]);

  useEffect(() => {
    if (!selected && pendingName) setSearch(pendingName);
  }, [pendingName, selected]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return projects.slice(0, 20);
    return projects
      .filter((c) => c.account_name.toLowerCase().includes(s))
      .slice(0, 20);
  }, [search, projects]);

  const displayValue = selected ? selected.account_name : search;
  const inputClassName = pendingName && !selected
    ? "border-red-300 bg-red-50"
    : "";

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={displayValue}
        placeholder={pendingName ? `unmatched: ${pendingName}` : "search project…"}
        onChange={(e) => {
          if (selected) onChange(null);
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className={inputClassName}
      />
      {selected && selected.monitor_keywords.length > 0 && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
          <Badge tone="green">{selected.monitor_keywords.length} kw</Badge>
        </div>
      )}
      {selected && selected.monitor_keywords.length === 0 && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
          <Badge tone="yellow">no kw</Badge>
        </div>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-stone-300 bg-white shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-2 py-2 text-xs text-stone-500">
              No project matches “{search}”. Add one on the Projects page.
            </li>
          ) : (
            matches.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between px-2 py-1.5 hover:bg-stone-100 cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(c.id);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <span className="truncate">{c.account_name}</span>
                <span className="text-xs text-stone-500 ml-2 whitespace-nowrap">
                  {c.monitor_keywords.length === 0
                    ? "no keywords"
                    : `${c.monitor_keywords.length} keyword${c.monitor_keywords.length === 1 ? "" : "s"}`}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// KOC picker: like ProjectPicker but supports auto-create on type — when the user submits
// a name not in the list, the server creates a new KOC. The picker captures the typed
// string in `koc_name_typed` so the parent can decide to send `koc_name` vs `koc_id`.
function KocPicker({
  kocs,
  value,
  typed,
  pendingName,
  onPick,
  onTyped,
}: {
  kocs: KocListItem[];
  value: number | null;
  typed: string;
  pendingName: string | null;
  onPick: (kocId: number) => void;
  onTyped: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = kocs.find((c) => c.id === value) ?? null;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const inputValue = selected ? selected.name : typed;
  const matches = useMemo(() => {
    const s = (selected ? "" : typed).trim().toLowerCase();
    if (!s) return kocs.slice(0, 20);
    return kocs.filter((k) => k.name.toLowerCase().includes(s)).slice(0, 20);
  }, [typed, kocs, selected]);

  const willAutoCreate =
    !selected && typed.trim().length > 0 && !kocs.some((k) => k.name.toLowerCase() === typed.trim().toLowerCase());

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={inputValue}
        placeholder={pendingName ? `unmatched: ${pendingName} (will auto-create)` : "pick or type new KOC name"}
        onChange={(e) => {
          onTyped(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {willAutoCreate && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
          <Badge tone="yellow">new</Badge>
        </div>
      )}
      {selected && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none">
          <Badge tone="blue">existing</Badge>
        </div>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-stone-300 bg-white shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-2 py-2 text-xs text-stone-500">
              No KOC matches — submitting will auto-create.
            </li>
          ) : (
            matches.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between px-2 py-1.5 hover:bg-stone-100 cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(c.id);
                  setOpen(false);
                }}
              >
                <span className="truncate">{c.name}</span>
                <span className="text-xs text-stone-500 ml-2 whitespace-nowrap">
                  {c.video_count} videos
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export function VideoImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"manual" | "file">("manual");
  const [rows, setRows] = useState<DraftRow[]>([emptyRow()]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [parsedRowErrors, setParsedRowErrors] = useState<string[]>([]);
  const [unresolvedProjects, setUnresolvedProjects] = useState(0);
  const [response, setResponse] = useState<VideoImportResponse | null>(null);
  // Per-row URL errors returned by the server's 400 response. Keyed by the original row
  // index in `rows`, populated in submit.onError after mapping `videos[i].url` issues
  // back through `submitIndexMap` (since validRowsWithIdx is filtered).
  const [serverUrlErrors, setServerUrlErrors] = useState<Record<number, string>>({});
  // Per-row outcome from a 201 import response, keyed by original row index — drives the
  // inline status badge ("added" / "duplicate" / "failed") shown under each row.
  const [rowResults, setRowResults] = useState<Record<number, VideoImportRowResult>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Maps the i-th submitted item back to its original row index in `rows`. Refreshed
  // every time submit.mutate() fires.
  const submitIndexMap = useRef<number[]>([]);

  const projectsQuery = useQuery({
    queryKey: ["projects-all"],
    queryFn: () => apiProjects.list().then((r) => r.items),
  });
  const projects = projectsQuery.data ?? [];

  const kocsQuery = useQuery({
    queryKey: ["kocs-all"],
    queryFn: () => apiKocs.list().then((r) => r.items),
  });
  const kocs = kocsQuery.data ?? [];

  const validRowsWithIdx = useMemo(
    () =>
      rows
        .map((r, idx) => ({ r, idx }))
        .filter(({ r }) => {
          if (r.project_id == null) return false;
          if (!r.url.trim()) return false;
          if (!PLATFORMS.includes(r.platform)) return false;
          const hasKoc = r.koc_id != null || r.koc_name_typed.trim().length > 0;
          return hasKoc;
        }),
    [rows],
  );
  const validRowCount = validRowsWithIdx.length;

  // Run validateUrl on every non-empty row; the result keys back to the original row
  // index so we can paint the error inline. Server errors are layered on top — server
  // wins because it reflects the actual rejected payload.
  const clientUrlErrors = useMemo(() => {
    const out: Record<number, string> = {};
    rows.forEach((r, i) => {
      const e = validateUrl(r.url);
      if (e) out[i] = e;
    });
    return out;
  }, [rows]);
  const urlErrorAt = (i: number): string | null =>
    clientUrlErrors[i] ?? serverUrlErrors[i] ?? null;
  const hasBlockingUrlError = validRowsWithIdx.some(({ idx }) => idx in clientUrlErrors);

  const submit = useMutation({
    mutationFn: () => {
      const items: VideoImportItem[] = validRowsWithIdx.map(({ r }) => {
        const base = {
          project_id: r.project_id as number,
          platform: r.platform,
          url: r.url.trim(),
          page_label: r.page_label.trim() || null,
        };
        if (r.koc_id != null) {
          return { ...base, koc_id: r.koc_id } as VideoImportItem;
        }
        return { ...base, koc_name: r.koc_name_typed.trim() } as VideoImportItem;
      });
      submitIndexMap.current = validRowsWithIdx.map(({ idx }) => idx);
      setServerUrlErrors({});
      setRowResults({});
      return apiVideos.import({ videos: items });
    },
    onSuccess: (resp) => {
      setResponse(resp);
      const next: Record<number, VideoImportRowResult> = {};
      resp.results.forEach((r, validIdx) => {
        const origIdx = submitIndexMap.current[validIdx];
        if (typeof origIdx === "number") next[origIdx] = r;
      });
      setRowResults(next);
      qc.invalidateQueries({ queryKey: ["videos"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["kocs"] });
    },
    onError: (err) => {
      // The server flatten()s url errors onto a top-level `videos: ["Invalid url"]`,
      // which loses the index. We also send the raw `issues` array (with paths like
      // ["videos", N, "url"]) so we can map each one back to a specific row.
      const body = (err as { body?: unknown }).body as
        | { error?: { issues?: Array<{ path: (string | number)[]; message: string }> } }
        | undefined;
      const issues = body?.error?.issues;
      if (!Array.isArray(issues)) return;
      const next: Record<number, string> = {};
      for (const it of issues) {
        if (
          it.path[0] === "videos" &&
          typeof it.path[1] === "number" &&
          it.path[2] === "url"
        ) {
          const validIdx = it.path[1];
          const origIdx = submitIndexMap.current[validIdx];
          if (typeof origIdx === "number") next[origIdx] = it.message;
        }
      }
      if (Object.keys(next).length > 0) setServerUrlErrors(next);
    },
  });

  function update(idx: number, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    if (patch.url !== undefined) {
      setServerUrlErrors((prev) => {
        if (!(idx in prev)) return prev;
        const next = { ...prev };
        delete next[idx];
        return next;
      });
    }
    setRowResults((prev) => {
      if (!(idx in prev)) return prev;
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  }
  function remove(idx: number) {
    setRows((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((_, i) => i !== idx)));
    setServerUrlErrors((prev) => {
      if (!(idx in prev)) return prev;
      const next = { ...prev };
      delete next[idx];
      return next;
    });
    // Shift entries above `idx` down by one so they stay attached to the right row.
    setRowResults((prev) => {
      const next: Record<number, VideoImportRowResult> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki === idx) continue;
        next[ki > idx ? ki - 1 : ki] = v;
      }
      return next;
    });
  }

  function parseCsv(text: string): { rows: ParsedTabularRow[]; rowErrors: string[]; headerError?: string } {
    const lines = text.replace(/\r\n?/g, "\n").trim().split("\n").filter((l) => l.length > 0);
    if (lines.length === 0) return { rows: [], rowErrors: [], headerError: "empty file" };

    const headerLine = lines[0];
    if (!headerLine) return { rows: [], rowErrors: [], headerError: "empty file" };
    const header = parseCsvLine(headerLine).map(matchHeader);
    const idxProject = header.indexOf("project");
    const idxKoc = header.indexOf("koc");
    const idxPlatform = header.indexOf("platform");
    const idxLabel = header.indexOf("label");
    const idxUrl = header.indexOf("url");
    if (idxProject < 0 || idxPlatform < 0 || idxUrl < 0) {
      return {
        rows: [],
        rowErrors: [],
        headerError:
          "header must include project/platform/url columns (koc and label are optional; also accepts: creator, account_name, handle, video_url, 项目, 平台, 标签, 视频链接)",
      };
    }

    const rows: ParsedTabularRow[] = [];
    const rowErrors: string[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      const cells = parseCsvLine(line);
      const project = (cells[idxProject] ?? "").trim();
      const koc = idxKoc >= 0 ? (cells[idxKoc] ?? "").trim() : "";
      const platformRaw = (cells[idxPlatform] ?? "").trim();
      const label = idxLabel >= 0 ? (cells[idxLabel] ?? "").trim() : "";
      const url = (cells[idxUrl] ?? "").trim();
      const platform = normalizePlatform(platformRaw);
      if (!project || !platform || !url) {
        rowErrors.push(`row ${i + 1}: missing project/platform/url`);
        continue;
      }
      rows.push({ project, koc, platform, label, url });
    }
    return { rows, rowErrors };
  }

  async function handleFile(file: File) {
    setFileError(null);
    setParsedRowErrors([]);
    setUnresolvedProjects(0);

    if (projectsQuery.isPending || kocsQuery.isPending) {
      setFileError("projects/kocs are still loading — please wait a moment and re-upload");
      return;
    }

    const lower = file.name.toLowerCase();
    try {
      let parsed: ParsedTabularRow[] = [];
      let rowErrors: string[] = [];
      if (lower.endsWith(".csv")) {
        const text = await file.text();
        const r = parseCsv(text);
        if (r.headerError) {
          setFileError(r.headerError);
          return;
        }
        parsed = r.rows;
        rowErrors = r.rowErrors;
      } else if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) {
        const sheet = await readXlsxFile(file);
        if (sheet.length === 0) {
          setFileError("empty sheet");
          return;
        }
        const headerCells = (sheet[0] ?? []).map((c) => String(c ?? ""));
        const header = headerCells.map(matchHeader);
        const idxProject = header.indexOf("project");
        const idxKoc = header.indexOf("koc");
        const idxPlatform = header.indexOf("platform");
        const idxLabel = header.indexOf("label");
        const idxUrl = header.indexOf("url");
        if (idxProject < 0 || idxPlatform < 0 || idxUrl < 0) {
          setFileError(
            "header must include project/platform/url columns (koc and label are optional; also accepts: creator, account_name, handle, video_url, 项目, 平台, 标签, 视频链接)",
          );
          return;
        }
        for (let i = 1; i < sheet.length; i++) {
          const row = sheet[i] ?? [];
          const project = String(row[idxProject] ?? "").trim();
          const koc = idxKoc >= 0 ? String(row[idxKoc] ?? "").trim() : "";
          const platformRaw = String(row[idxPlatform] ?? "").trim();
          const label = idxLabel >= 0 ? String(row[idxLabel] ?? "").trim() : "";
          const url = String(row[idxUrl] ?? "").trim();
          const platform = normalizePlatform(platformRaw);
          if (!project || !platform || !url) {
            rowErrors.push(`row ${i + 1}: missing project/platform/url`);
            continue;
          }
          parsed.push({ project, koc, platform, label, url });
        }
      } else {
        setFileError("unsupported file type — please upload .csv, .xls, or .xlsx");
        return;
      }

      const { rows: drafts, unresolvedProjects } = tabularToDrafts(parsed, projects, kocs);
      setParsedRowErrors(rowErrors);
      setUnresolvedProjects(unresolvedProjects);
      setRows(drafts.length > 0 ? drafts : [emptyRow()]);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : String(err));
    }
  }

  const summary =
    response && (
      <div className="rounded border border-stone-200 bg-stone-50 p-3 mb-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone="green">{response.inserted} inserted</Badge>
          {response.duplicates > 0 && (
            <Badge tone="yellow">
              {response.duplicates} duplicate{response.duplicates === 1 ? "" : "s"}
            </Badge>
          )}
          {response.errors > 0 && (
            <Badge tone="red">
              {response.errors} error{response.errors === 1 ? "" : "s"}
            </Badge>
          )}
          <span className="text-xs text-stone-500">— see status on each row below</span>
        </div>
      </div>
    );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-[52rem] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Add / import videos</h2>
          <Button variant="ghost" onClick={onClose} className="!px-2">×</Button>
        </div>

        <p className="text-xs text-stone-500 mb-3">
          Pick the owning project for each video. The scraper runs the project's monitor
          keywords against the scraped title/description — if any match, t+24h and t+7d
          follow-up snapshots are scheduled (same rule as the daily scrape). Videos with no
          keyword match still land in the table but without follow-ups. To enable follow-ups
          for a new project, add it on the Projects page first with its keywords. Each row
          also requires a KOC — pick an existing one or type a new name to auto-create.
        </p>

        <div className="flex gap-3 mb-3 text-sm">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={mode === "manual"}
              onChange={() => setMode("manual")}
            />
            Manual entry
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={mode === "file"}
              onChange={() => setMode("file")}
            />
            Upload CSV / XLSX
          </label>
        </div>

        {mode === "file" && (
          <div className="mb-3 space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              className="block text-sm"
            />
            <p className="text-xs text-stone-500">
              Required columns: <code>project</code>, <code>platform</code>, <code>url</code>.
              Optional: <code>koc</code> (KOC name — auto-creates a new KOC if not in the
              existing list), <code>label</code> (page label, picks/creates a specific page
              on (project, platform)). Project names are matched against existing projects
              by <code>account_name</code>. Platform values:
              {" "}<code>tiktok</code>, <code>instagram</code>, <code>youtube</code>.
            </p>
            {fileError && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {fileError}
              </div>
            )}
            {parsedRowErrors.length > 0 && (
              <details className="text-xs text-stone-600">
                <summary className="cursor-pointer">
                  Skipped {parsedRowErrors.length} rows during parsing
                </summary>
                <ul className="mt-1 ml-4 list-disc">
                  {parsedRowErrors.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            )}
            {unresolvedProjects > 0 && (
              <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                {unresolvedProjects} row{unresolvedProjects === 1 ? "" : "s"} couldn't auto-match a project
                — pick one manually below or remove the row.
              </div>
            )}
          </div>
        )}

        {summary}

        {projectsQuery.isPending ? (
          <div className="text-xs text-stone-500 mb-3">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mb-3">
            No projects yet. Add one on the Projects page first (with monitor keywords if you
            want t+24h / t+7d follow-ups to fire).
          </div>
        ) : null}

        <div className="space-y-2 mb-3">
          <div className="grid grid-cols-[1fr_1fr_6rem_8rem_1fr_2.5rem] gap-2 text-xs font-medium text-stone-700">
            <div>Project</div>
            <div>KOC</div>
            <div>Platform</div>
            <div>Label <span className="text-stone-400 font-normal">(optional)</span></div>
            <div>Video URL</div>
            <div></div>
          </div>
          {rows.map((r, i) => {
            const pickedProject = r.project_id != null ? projects.find((c) => c.id === r.project_id) : null;
            const labelSuggestions = pickedProject
              ? Array.from(
                  new Set(
                    pickedProject.pages
                      .filter((a) => a.platform === r.platform && a.label)
                      .map((a) => a.label as string),
                  ),
                )
              : [];
            const datalistId = `labels-${i}`;
            const result = rowResults[i];
            const rowTintClass = result
              ? result.status === "error"
                ? "border border-red-200 bg-red-50 rounded p-2"
                : result.status === "duplicate"
                  ? "border border-yellow-200 bg-yellow-50 rounded p-2"
                  : "border border-green-200 bg-green-50 rounded p-2"
              : "";
            return (
              <div key={i} className={`space-y-1 ${rowTintClass}`}>
                <div className="grid grid-cols-[1fr_1fr_6rem_8rem_1fr_2.5rem] gap-2 items-start">
                  <ProjectPicker
                    projects={projects}
                    value={r.project_id}
                    pendingName={r.pendingProjectName}
                    onChange={(projectId) =>
                      update(i, { project_id: projectId, pendingProjectName: null })
                    }
                  />
                  <KocPicker
                    kocs={kocs}
                    value={r.koc_id}
                    typed={r.koc_name_typed}
                    pendingName={r.pendingKocName}
                    onPick={(kocId) =>
                      update(i, { koc_id: kocId, koc_name_typed: "", pendingKocName: null })
                    }
                    onTyped={(name) =>
                      update(i, { koc_id: null, koc_name_typed: name, pendingKocName: null })
                    }
                  />
                  <select
                    value={r.platform}
                    onChange={(e) => update(i, { platform: e.target.value as Platform })}
                    className="block w-full rounded border border-stone-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-400"
                  >
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                    <option value="youtube">YouTube</option>
                  </select>
                  <div>
                    <Input
                      value={r.page_label}
                      onChange={(e) => update(i, { page_label: e.target.value })}
                      placeholder={labelSuggestions.length > 0 ? labelSuggestions[0] : "Main, Brand…"}
                      list={labelSuggestions.length > 0 ? datalistId : undefined}
                    />
                    {labelSuggestions.length > 0 && (
                      <datalist id={datalistId}>
                        {labelSuggestions.map((l) => (
                          <option key={l} value={l} />
                        ))}
                      </datalist>
                    )}
                  </div>
                  <div>
                    <Input
                      value={r.url}
                      onChange={(e) => update(i, { url: e.target.value })}
                      placeholder="https://…"
                      className={urlErrorAt(i) ? "border-red-400 bg-red-50" : ""}
                    />
                    {urlErrorAt(i) && (
                      <div className="mt-0.5 text-xs text-red-700">{urlErrorAt(i)}</div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => remove(i)}
                    className="!px-2"
                  >
                    ×
                  </Button>
                </div>
                {pickedProject && pickedProject.monitor_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 ml-1 text-xs text-stone-500">
                    <span>matches:</span>
                    {pickedProject.monitor_keywords.map((k) => (
                      <Badge key={k}>{k}</Badge>
                    ))}
                  </div>
                )}
                {result && (
                  <div className="flex flex-wrap items-center gap-2 ml-1 text-xs">
                    {result.status === "inserted" && (
                      <>
                        <Badge tone="green">added</Badge>
                        <span className="text-stone-700">
                          video #{result.video_id} • scrape job queued
                        </span>
                      </>
                    )}
                    {result.status === "duplicate" && (
                      <>
                        <Badge tone="yellow">duplicate</Badge>
                        <span className="text-stone-700">
                          already imported
                          {result.video_id ? ` as video #${result.video_id}` : ""}
                          {result.existing_project_name || result.existing_project_id != null
                            ? ` under project ${result.existing_project_name ?? "?"}${
                                result.existing_project_id != null ? ` (#${result.existing_project_id})` : ""
                              }`
                            : ""}
                          {result.existing_koc_name || result.existing_koc_id != null
                            ? ` / koc ${result.existing_koc_name ?? "?"}${
                                result.existing_koc_id != null ? ` (#${result.existing_koc_id})` : ""
                              }`
                            : ""}
                          {" — skipped"}
                        </span>
                      </>
                    )}
                    {result.status === "error" && (
                      <>
                        <Badge tone="red">failed</Badge>
                        <span className="text-red-700 break-all">{result.error}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {mode === "manual" && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRows([...rows, emptyRow()])}
            className="mb-3"
          >
            + Add row
          </Button>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-stone-200">
          <div className="text-xs text-stone-500">
            {validRowCount} valid row{validRowCount === 1 ? "" : "s"}
            {hasBlockingUrlError && (
              <span className="ml-2 text-red-700">
                — fix URL errors above before importing
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              {response ? "Done" : "Cancel"}
            </Button>
            <Button
              onClick={() => submit.mutate()}
              disabled={validRowCount === 0 || hasBlockingUrlError || submit.isPending}
            >
              {submit.isPending
                ? "Importing…"
                : `Import ${validRowCount} video${validRowCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>

        {submit.isError && (
          <div className="mt-2 text-xs text-red-700">
            {Object.keys(serverUrlErrors).length > 0
              ? `${Object.keys(serverUrlErrors).length} row${Object.keys(serverUrlErrors).length === 1 ? "" : "s"} rejected by the server — see highlighted URL inputs above.`
              : submit.error instanceof Error
                ? submit.error.message
                : "Import failed"}
          </div>
        )}
      </Card>
    </div>
  );
}
